import { LitElement, css, html, nothing } from "lit";
import { map } from "lit/directives/map.js";
import { repeat } from "lit/directives/repeat.js";
import { join } from "lit/directives/join.js";
import { classMap } from "lit/directives/class-map.js";
import { customElement, property, state } from "lit/decorators.js";
import { EditorView } from "prosemirror-view";

import "@shoelace-style/shoelace/dist/components/menu/menu.js";
import "@shoelace-style/shoelace/dist/components/menu-label/menu-label.js";
import "@shoelace-style/shoelace/dist/components/menu-item/menu-item.js";
import "@shoelace-style/shoelace/dist/components/divider/divider.js";

import { SlPopup } from "@shoelace-style/shoelace";

import "@shoelace-style/shoelace/dist/themes/light.styles.js";

import { MenuItemState } from "../../../core/menu";
import { type MatchInput } from "..";
import { SubMenu } from "./insert-submenu";

/**
 * Overwrite the run type for MenuItemState.
 */
export type InputMenuItemState = Omit<
    MenuItemState,
    "run" | "items" | "getItems"
> & {
    run: (event: Event, input: MatchInput) => void;
    items: Omit<InputMenuItemState, "group" | "showInInlineMenu">[];
    getItems: () => Promise<InputMenuItemState["items"]>;
};

/**
 * Returns an icon template from the given name.
 */
export const iconTemplate = ({
    name,
    library = "component-assets",
    slot = "prefix",
}: {
    name: string;
    library?: string;
    slot?: string;
}) => html` <sl-icon name=${name} library=${library} slot=${slot}></sl-icon>`;

/**
 * Returns the menu items sorted based on the order property.
 */
export const sortItems = <T extends { order?: number }>(
    items: T[] = []
): T[] => {
    return items.toSorted((a, b) => {
        return (
            (a.order ?? Number.MAX_SAFE_INTEGER) -
            (b.order ?? Number.MAX_SAFE_INTEGER)
        );
    });
};

@customElement("c-node-insert-menu")
class NodeInsertMenu extends LitElement {
    @state()
    selection: {
        groupIndex: number;
        itemIndex: number;
    } = { groupIndex: 0, itemIndex: 0 };

    @state()
    input: MatchInput;

    @property()
    editorView: EditorView;

    @property()
    items: InputMenuItemState[];

    private filteredItems: InputMenuItemState[];

    constructor(
        items: InputMenuItemState[],
        input: MatchInput,
        editorView: EditorView
    ) {
        super();
        this.input = input;
        this.items = items;
        this.filteredItems = this.items;
        this.editorView = editorView;
    }

    updated() {
        if (this.filteredItems.length === 1) {
            this.openSubmenu();
        }
    }

    private get itemsPerGroup() {
        return Object.groupBy(sortItems(this.filteredItems), (item) =>
            item.group === "base" ? "" : item.group
        );
    }

    private get groupKeys() {
        return Object.keys(this.itemsPerGroup);
    }

    private get currentGroupKey() {
        return this.groupKeys.at(this.selection.groupIndex);
    }

    private get currentGroup() {
        return this.itemsPerGroup[this.currentGroupKey];
    }

    private get currentItem() {
        return this.currentGroup[this.selection.itemIndex];
    }

    private get currentSelector() {
        return (
            `[data-group="${this.currentGroupKey}"]` +
            `[data-index="${this.selection.itemIndex}"]`
        );
    }

    private get currentDom() {
        return this.renderRoot.querySelector(this.currentSelector);
    }

    private get subMenuSlPopup() {
        return this.currentDom?.shadowRoot?.querySelector<SlPopup>("sl-popup");
    }

    private get subInsertSubmenu() {
        const submenu = this.currentDom?.querySelector<SubMenu>(
            "c-node-insert-submenu"
        );
        return submenu instanceof SubMenu ? submenu : null;
    }

    down() {
        if (this.subMenuSlPopup?.active) {
            this.subInsertSubmenu.down();
            return;
        }

        if (this.selection.itemIndex < this.currentGroup.length - 1) {
            // Still room within current group.
            this.selection.itemIndex++;
        } else {
            if (this.selection.groupIndex < this.groupKeys.length - 1) {
                // Jump to next group.
                this.selection.groupIndex++;
                this.selection.itemIndex = 0;
            } else {
                // Begin from start.
                this.selection = { groupIndex: 0, itemIndex: 0 };
            }
        }
        this.requestUpdate();
        return this.currentItem;
    }

    up() {
        if (this.subMenuSlPopup?.active) {
            this.subInsertSubmenu.up();
            return;
        }

        if (this.selection.itemIndex > 0) {
            // Still room within current group.
            this.selection.itemIndex--;
        } else {
            if (this.selection.groupIndex > 0) {
                // Jump to previous group.
                this.selection.groupIndex--;
                this.selection.itemIndex = this.currentGroup.length - 1;
            } else {
                // Wrap around to the end.
                this.selection.groupIndex = this.groupKeys.length - 1;
                this.selection.itemIndex = this.currentGroup.length - 1;
            }
        }
        this.requestUpdate();
        return this.currentItem;
    }

    openSubmenu() {
        if (
            this.currentItem.type === "dropdown" &&
            this.subMenuSlPopup &&
            !this.subMenuSlPopup.active
        ) {
            this.closeAllSubmenus();
            this.subMenuSlPopup.active = true;
            this.subMenuSlPopup.focus();
            return true;
        }
        return false;
    }

    closeSubmenu() {
        if (
            this.currentItem.type === "dropdown" &&
            this.subMenuSlPopup?.active
        ) {
            this.subMenuSlPopup.active = false;
            return true;
        }
        return false;
    }

    closeAllSubmenus() {
        this.renderRoot.querySelectorAll("sl-menu-item").forEach((element) => {
            const popup =
                element.shadowRoot?.querySelector<SlPopup>("sl-popup");
            if (popup) {
                popup.active = false;
            }
        });
    }

    runSelection(event: Event) {
        if (this.currentItem.type === "dropdown") {
            if (this.subMenuSlPopup?.active) {
                this.subInsertSubmenu.runSelection(event);
            } else {
                this.openSubmenu();
            }
        } else {
            this.currentItem.run(event, this.input);
        }
    }

    render() {
        return html`
            <sl-menu>
                ${join(
                    map(
                        Object.entries(this.itemsPerGroup),
                        ([group, items], groupIndex) => {
                            return html`${group
                                ? html`<sl-menu-label>${group}</sl-menu-label>`
                                : nothing}
                            ${repeat(
                                items,
                                (item) => item.name,
                                (item, itemIndex) =>
                                    html`<sl-menu-item
                                        ?disabled=${item.disabled}
                                        @mouseenter=${() => {
                                            this.selection = {
                                                groupIndex,
                                                itemIndex,
                                            };
                                            this.openSubmenu();
                                            this.editorView.focus();
                                        }}
                                        @focus=${() => {
                                            this.selection = {
                                                groupIndex,
                                                itemIndex,
                                            };
                                            this.editorView.focus();
                                        }}
                                        @click=${(e: MouseEvent) => {
                                            if (item.run) {
                                                item.run(e, this.input);
                                            }
                                        }}
                                        class=${classMap({
                                            selected:
                                                groupIndex ===
                                                    this.selection.groupIndex &&
                                                itemIndex ===
                                                    this.selection.itemIndex,
                                        })}
                                        title=${item.label}
                                        data-group=${group}
                                        data-index=${itemIndex}
                                    >
                                        ${item.icon
                                            ? iconTemplate({
                                                  ...item.icon,
                                                  slot: "prefix",
                                              })
                                            : nothing}
                                        ${item.label || ""}
                                        ${item.type === "dropdown"
                                            ? html`
                                                  <c-node-insert-submenu
                                                      slot="submenu"
                                                      .input=${this.input}
                                                      .item=${item}
                                                  ></c-node-insert-submenu>
                                              `
                                            : nothing}
                                    </sl-menu-item>`
                            )}`;
                        }
                    ),
                    html`<sl-divider></sl-divider>`
                )}
            </sl-menu>
        `;
    }

    static styles = css`
        sl-menu-item:focus-visible::part(base),
        sl-menu-item.selected::part(base) {
            background: var(--stekz-secondary-background);
            color: var(--text-color);
        }

        sl-menu-item.selected[disabled]::part(base) {
            background-color: var(--sl-color-neutral-100);
            color: var(--text-color);
        }
    `;
}

export { NodeInsertMenu };
